import React, { useCallback } from "react";
import { HeaderRoot, Divider, CustomButtom } from "./Header.styles";

import { Box, Typography, useTheme } from "@mui/material";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import dynaViewLogo from "../../assets/dynaview-logo.png";
import { findByDescriptor } from "../../helpers/findItems";
import { generateRandomConfiguration } from "../../helpers/randomizer";
import { useSelectedOptions } from "../../context/SelectedOptionsContext";

export const Header = ({ emitUIInteraction, actionsList }) => {
  const theme = useTheme();
  const { selectOption, selectMenuOption, currentMenuOption } =
    useSelectedOptions();

  const isReady = typeof emitUIInteraction === "function";

  const clickOnAction = useCallback(
    (descriptor) => {
      if (typeof emitUIInteraction === "function") {
        emitUIInteraction(descriptor);
      } else {
        console.warn("emitUIInteraction is not ready yet", emitUIInteraction);
      }
    },
    [emitUIInteraction]
  );

  const handleRandomize = useCallback(() => {
    // 1. Generate the random descriptors map (e.g., { FrLi: "wcf", ExPt: "prm", ... })
    const randomDescriptors = generateRandomConfiguration();

    // 2. Iterate and select each randomized option
    Object.entries(randomDescriptors).forEach(([groupKey, descriptorValue]) => {
      // Construct the descriptor object (e.g., { "FrLi": "wcf" })
      const descriptor = { [groupKey]: descriptorValue };

      // Get the full context object: { level1, sub, node } (using default selector)
      const optionContext = findByDescriptor(actionsList, descriptor);

      if (optionContext && optionContext.node) {
        const { node, sub } = optionContext;

        // 3. CRITICAL: Map the context data into the required flat shape for selectOption
        const payload = {
          // Required properties, typically found on the 'node'
          name: node.name,
          image: node.image,
          price: node.price,

          // Properties often nested or on related objects
          descriptor: node.action?.descriptor || descriptor, // Use descriptor from action or fall back to the generated one
          baseOption: node.action?.baseOption || node.baseOption, // Find base option
          inSummary: node.action?.inSummary || node.inSummary, // Find inSummary status

          // SubCategory name comes from the 'sub' action wrapper
          subCategory: sub?.name,

          // Note: The context internally calculates groupKey from the descriptor.
        };

        // 4. Update the context with the correctly shaped payload
        selectOption(payload);
        clickOnAction(randomDescriptors);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionsList, selectOption, emitUIInteraction]); // actionsList and the stable selectOption

  return (
    <HeaderRoot position="static">
      <Box
        sx={{
          background: theme?.palette?.companyColors?.primary,
          padding: { xs: "1rem 1.25rem", lg: "1.5rem 3rem" },
        }}
      >
        <Box
          component="img"
          src={dynaViewLogo}
          alt="Fallback"
          sx={{
            width: "auto",
            height: {
              xs: "1.75rem",
              md: "2.5rem",
            },
            display: "block",
            zIndex: 1,
          }}
          // ref={targetRef}
        />
      </Box>
      <Typography
        variant="h5"
        sx={{
          display: {
            xs: "none",
            md: "block",
          },
          textAlign: "left",
          position: "relative",
          lineHeight: 1,
          fontWeight: 400,
          padding: "0 4rem",
          color: theme?.palette?.companyColors?.primary,
        }}
      >
        Office design proposal
      </Typography>
      <Divider
        sx={{
          display: {
            xs: "none",
            md: "block",
          },
        }}
      />
      <CustomButtom
        variant="text"
        onClick={() => selectMenuOption("options")}
        active={currentMenuOption === "options"}
      >
        Options
      </CustomButtom>
      <Divider />
      <CustomButtom
        variant="text"
        onClick={() => selectMenuOption("media")}
        active={currentMenuOption === "media"}
      >
        Media
      </CustomButtom>
      <Divider
        sx={{
          display: {
            xs: "none",
            md: "block",
          },
        }}
      />
      <CustomButtom
        startIcon={<ShuffleIcon />}
        variant="text"
        onClick={handleRandomize}
        sx={{
          width: { xs: "auto", lg: "15rem" },
          right: 0,
          top: 0,
          bottom: 0,
          background: theme.palette.companyColors.accent,
          color: theme.palette.companyColors.secondary,
          position: { xs: "absolute", lg: "relative" },
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            background: theme?.palette?.companyColors?.secondary,
            color: theme?.palette?.companyColors?.accent,
          },
          opacity: isReady ? 1 : 0.5,
          pointerEvents: isReady ? "auto" : "none",
        }}
      >
        Randomizer
      </CustomButtom>
      <Divider
        sx={{
          display: {
            xs: "none",
            md: "block",
          },
        }}
      />
    </HeaderRoot>
  );
};
